import React, { useEffect, useRef, useState } from 'react';

import { Loader } from '../../../components';
import Button from '../../../components/Buttons/Button';
import cn from '../../../lib/classNames';

import HeaderBgVideo from './images/headerBgVideo.mp4';

import styles from './Header.module.scss';

type HeaderType = {
	classPageContent?: string;
	onDemoButtonVisibilityChange?: (isVisible: boolean) => void;
	onLoadingComplete?: (isLoading: boolean) => void;
};

const Header = React.forwardRef<HTMLDivElement, HeaderType>((props, ref) => {
	const { classPageContent, onDemoButtonVisibilityChange, onLoadingComplete } = props;
	const [isLoading, setIsLoading] = useState(true);
	const demoButtonRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (onDemoButtonVisibilityChange) {
					onDemoButtonVisibilityChange(entry.isIntersecting);
				}
			},
			{ root: null, threshold: 0.1 },
		);

		if (demoButtonRef.current) {
			observer.observe(demoButtonRef.current);
		}

		return () => {
			if (demoButtonRef.current) {
				observer.unobserve(demoButtonRef.current);
			}
		};
	}, [onDemoButtonVisibilityChange]);

	useEffect(() => {
		if (onLoadingComplete) {
			onLoadingComplete(isLoading);
		}
	}, [isLoading, onLoadingComplete]);

	return (
		<div ref={ref} className={cn(styles['header-wrap'], { [styles.loading]: isLoading })}>
			<Loader className={styles.loader} />
			<header className={styles.header}>
				<video
					className={styles['header-video']}
					autoPlay
					loop
					muted
					playsInline
					onCanPlayThrough={() => setIsLoading(false)}
				>
					<source src={HeaderBgVideo} type="video/mp4" />
				</video>

				<div className={styles.content}>
					<div className={cn(styles['title-block'], classPageContent)}>
						<h1 className={styles.title}>Safe-xplore Business Edition</h1>
						<p className={styles.subtitle}>
							Advanced Travel Risk Intelligence for Growing Organizations
						</p>
						<div ref={demoButtonRef} className={styles['button-wrap']}>
							<Button
								className={cn(styles.button, 'button-book-the-demo')}
								elementType="button"
								variant="filled"
								color="secondary"
								size="large"
								// Disabled due to the Ortto form usage
								// elementType="link"
								// href="https://safe-esteem.typeform.com/to/UVWEowBk"
								// target="_blank"
							>
								Book the demo
							</Button>
						</div>
					</div>
				</div>
			</header>
		</div>
	);
});

Header.displayName = 'Header';

export default Header;
